
import React from 'react';
import { WeatherCondition } from '@/types/weather';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import WeatherIcon from '@/components/weather/WeatherIcon';
import { MessageSquareDot } from 'lucide-react';

interface WeatherConditionPickerProps {
  selectedCondition: WeatherCondition | null;
  onSelectCondition: (condition: WeatherCondition) => void;
}

interface ConditionOption {
  value: WeatherCondition;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  buttonBgColor: string;
}

const conditionOptions: ConditionOption[] = [
  { 
    value: 'clear', 
    label: 'Clear', 
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    buttonBgColor: 'bg-amber-100 hover:bg-amber-200'
  },
  { 
    value: 'partlyCloudy', 
    label: 'Partly\nCloudy', 
    color: 'text-blue-400',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    buttonBgColor: 'bg-blue-100 hover:bg-blue-200'
  },
  { 
    value: 'cloudy', 
    label: 'Cloudy', 
    color: 'text-slate-500',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    buttonBgColor: 'bg-slate-100 hover:bg-slate-200'
  },
  { 
    value: 'rain', 
    label: 'Rain', 
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    buttonBgColor: 'bg-cyan-100 hover:bg-cyan-200'
  },
  { 
    value: 'heavyRain', 
    label: 'Heavy\nRain', 
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    buttonBgColor: 'bg-blue-200 hover:bg-blue-300'
  },
  { 
    value: 'thunderstorm', 
    label: 'Thunderstorm', 
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    buttonBgColor: 'bg-indigo-100 hover:bg-indigo-200'
  },
  { 
    value: 'snow', 
    label: 'Snow', 
    color: 'text-blue-300',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-100',
    buttonBgColor: 'bg-sky-100 hover:bg-sky-200'
  },
  { 
    value: 'fog', 
    label: 'Fog', 
    color: 'text-gray-400',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    buttonBgColor: 'bg-gray-100 hover:bg-gray-200'
  },
  { 
    value: 'wind', 
    label: 'Wind', 
    color: 'text-teal-500',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    buttonBgColor: 'bg-teal-100 hover:bg-teal-200'
  },
  { 
    value: 'alert', 
    label: 'Weather\nAlert', 
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    buttonBgColor: 'bg-red-100 hover:bg-red-200'
  },
];

const WeatherConditionPicker: React.FC<WeatherConditionPickerProps> = ({ 
  selectedCondition, 
  onSelectCondition 
}) => {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 lg:grid-cols-3 gap-3">
        {conditionOptions.map((option) => (
          <Button
            key={option.value}
            type="button"
            variant="outline"
            className={cn(
              "flex flex-col items-center justify-center h-40 gap-2 transition-all",
              "hover:border-2 hover:shadow-md",
              "[&_svg]:size-8", // Updated to size-8 for larger icons
              option.buttonBgColor,
              selectedCondition === option.value 
                ? `border-2 ${option.borderColor} shadow-md` 
                : "border-gray-200"
            )}
            onClick={() => onSelectCondition(option.value)}
          >
            <div className={cn("transition-colors", option.color)}>
              <WeatherIcon 
                condition={option.value} 
                size="md" 
                animated={selectedCondition === option.value}
              />
            </div>
            <span className="text-sm font-medium text-center whitespace-pre-line leading-tight px-1 max-w-full">
              {option.label}
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
};

export default WeatherConditionPicker;
